/*
MIT License

Copyright (c) 2019 SIL LSDev

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/

import { ViewableVideoCollection } from '../video/ViewableVideoCollection'
import { AudioContextFactory, AudioWaveformCreator } from '../video/WaveformVisualizer'

import { Passage } from '../../models3/Passage'
import { PassageVideo } from '../../models3/PassageVideo'

export const mimeWav = 'audio/wav'

export interface WaveOptions {
    isFloat: boolean
    numChannels: number
    sampleRate: number
    numFrames?: number
}

export type Section = {
    start: number
    end: number
}

const isMonotonicallyIncreasing = (values: number[]) => {
    const TOLERANCE = 5000
    let previous = Number.MIN_SAFE_INTEGER
    for (const num of values) {
        if (previous - num > TOLERANCE) {
            return false
        }
        previous = num
    }
    return true
}

const interleave = (data: any[], options: WaveOptions) => {
    if (!data.length) {
        return []
    }

    const ArrayType = options.isFloat ? Float32Array : Uint16Array
    const interleaved: any = new ArrayType(data.reduce((acc, current) => acc + current.length, 0))
    for (let frame = 0; frame < data[0].length; frame++) {
        for (let channel = 0; channel < data.length; channel++) {
            const dst = frame * data.length + channel
            interleaved[dst] = data[channel][frame]
        }
    }
    return interleaved
}

const getAudioBuffer = async (audioContext: AudioContext, src: string) => {
    const response = await fetch(src)
    const buffer = await response.arrayBuffer()
    return audioContext.decodeAudioData(buffer)
}

const performDeletion = async (audioBuffer: AudioBuffer, sectionsToKeep: Section[]) => {
    if (sectionsToKeep.some((section) => section.end < section.start)) {
        throw new Error('Section end < section start')
    }

    // We don't allow for overlapping sections
    const positions = [0, ...sectionsToKeep.flatMap((section) => [section.start, section.end]), audioBuffer.length]
    if (!isMonotonicallyIncreasing(positions)) {
        throw new Error(`Positions are not monotonically increasing: ${positions}`)
    }

    const length = sectionsToKeep.reduce((sum, section) => sum + section.end - section.start, 0)
    if (length <= 0) {
        throw new Error('Buffer length must be > 0.')
    }

    const newBuffer = new AudioBuffer({
        length,
        numberOfChannels: 1,
        sampleRate: audioBuffer.sampleRate
    })

    const channelData = audioBuffer.getChannelData(0)
    let offset = 0
    for (let j = 0; j < sectionsToKeep.length; j++) {
        newBuffer.copyToChannel(channelData.slice(sectionsToKeep[j].start, sectionsToKeep[j].end), 0, offset)
        offset += sectionsToKeep[j].end - sectionsToKeep[j].start
    }

    return newBuffer
}

const concatenateFloat32Arrays = (arrays: Float32Array[]) => {
    const fullArrayLength = arrays.reduce((sum, array) => sum + array.length, 0)
    const singleArray = new Float32Array(fullArrayLength)
    let currentIndex = 0
    for (const array of arrays) {
        singleArray.set(array, currentIndex)
        currentIndex += array.length
    }
    return singleArray
}

// adapted from https://gist.github.com/also/900023
// returns Uint8Array of WAV header bytes
function getWavHeader(options: WaveOptions) {
    const numFrames = options.numFrames || 0
    const numChannels = options.numChannels || 2
    const sampleRate = options.sampleRate || 44100
    const bytesPerSample = options.isFloat ? 4 : 2
    const format = options.isFloat ? 3 : 1

    const blockAlign = numChannels * bytesPerSample
    const byteRate = sampleRate * blockAlign
    const dataSize = numFrames * blockAlign

    const buffer = new ArrayBuffer(44)
    const dv = new DataView(buffer)

    let p = 0

    function writeString(s: string) {
        for (let i = 0; i < s.length; i++) {
            dv.setUint8(p + i, s.charCodeAt(i))
        }
        p += s.length
    }

    function writeUint32(d: number) {
        dv.setUint32(p, d, true)
        p += 4
    }

    function writeUint16(d: number) {
        dv.setUint16(p, d, true)
        p += 2
    }

    writeString('RIFF') // ChunkID
    writeUint32(dataSize + 36) // ChunkSize
    writeString('WAVE') // Format
    writeString('fmt ') // Subchunk1ID
    writeUint32(16) // Subchunk1Size
    writeUint16(format) // AudioFormat
    writeUint16(numChannels) // NumChannels
    writeUint32(sampleRate) // SampleRate
    writeUint32(byteRate) // ByteRate
    writeUint16(blockAlign) // BlockAlign
    writeUint16(bytesPerSample * 8) // BitsPerSample
    writeString('data') // Subchunk2ID
    writeUint32(dataSize) // Subchunk2Size

    return new Uint8Array(buffer)
}

// Returns Uint8Array of WAV bytes
function getWavBytes(buffer: any, options: WaveOptions) {
    const type = options.isFloat ? Float32Array : Uint16Array
    options.numFrames = buffer.byteLength / type.BYTES_PER_ELEMENT

    const headerBytes = getWavHeader(options)
    const wavBytes = new Uint8Array(headerBytes.length + buffer.byteLength)

    // prepend header, then add pcmBytes
    wavBytes.set(headerBytes, 0)
    wavBytes.set(new Uint8Array(buffer), headerBytes.length)

    return wavBytes
}

async function convertToWav(data: any[], options: WaveOptions) {
    // raw/PCM buffer

    // get WAV file bytes and audio params of your audio source
    const wavBytes = getWavBytes(interleave(data, options).buffer, options)
    return new Blob([wavBytes], { type: 'audio/wav' })
}

export const fileToArrayBuffer = (file: File): Promise<ArrayBuffer> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onerror = function onerror(ev) {
            reject(ev.target?.error)
        }

        reader.onload = function onload(ev) {
            resolve(ev.target?.result as ArrayBuffer)
        }

        reader.readAsArrayBuffer(file)
    })
}

export const convertAudioBufferToWav = async (audioBuffer: AudioBuffer) => {
    const numChannels = audioBuffer.numberOfChannels
    const channelData = [...Array(numChannels).keys()].map((channel) => audioBuffer.getChannelData(channel))
    return convertToWav(channelData, {
        isFloat: true, // audio buffers contains array of Float32Array
        numChannels,
        sampleRate: audioBuffer.sampleRate
    })
}

export const convertArrayBufferToWav = async (arrayBuffer: ArrayBuffer) => {
    const audioContext = AudioContextFactory.getAudioContext()
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)
    const wavBlob = await convertAudioBufferToWav(audioBuffer)
    return wavBlob.arrayBuffer()
}

export const convertToWavArrayBuffer = async (input: Blob | File): Promise<ArrayBuffer> => {
    const arrayBuffer = input instanceof File ? await fileToArrayBuffer(input) : await input.arrayBuffer()
    if (input.type !== mimeWav) {
        return convertArrayBufferToWav(arrayBuffer)
    }
    return arrayBuffer
}

export const deleteAudioSection = async (audioContext: AudioContext, src: string, sectionsToKeepSeconds: Section[]) => {
    const audioBuffer = await getAudioBuffer(audioContext, src)
    const timeToSample = (time: number) => Math.round(time * audioBuffer.sampleRate)
    const sectionsToKeep = sectionsToKeepSeconds.map((section) => ({
        start: timeToSample(section.start),
        end: timeToSample(section.end)
    }))
    const buffer = await performDeletion(audioBuffer, sectionsToKeep)
    return convertAudioBufferToWav(buffer)
}

export const ambientNoise = (audioContext: AudioContext, lengthInSeconds: number) => {
    return audioContext.createBuffer(1, lengthInSeconds * audioContext.sampleRate, audioContext.sampleRate)
}

export const copyToNewBuffer = (channelDatas: Float32Array[], length: number, sampleRate: number) => {
    if (length <= 0) {
        throw new Error('Buffer length must be > 0.')
    }

    const newBuffer = new AudioBuffer({
        length,
        numberOfChannels: channelDatas.length,
        sampleRate
    })

    for (let i = 0; i < channelDatas.length; i++) {
        newBuffer.copyToChannel(channelDatas[i], i)
    }

    return newBuffer
}

export const createSingleBuffer = (buffers: AudioBuffer[]) => {
    const bufferLength = buffers.reduce((sum, buffer) => sum + buffer.length, 0)
    const firstChannel = concatenateFloat32Arrays(buffers.map((buf) => buf.getChannelData(0)))
    return copyToNewBuffer([firstChannel], bufferLength, 44100)
}

export const createPassageAudioBuffer = async (passage: Passage, passageVideo: PassageVideo) => {
    const waveformCreator = new AudioWaveformCreator()
    const vvc = new ViewableVideoCollection()
    const audioSlices = await passageVideo.getPlayableSlices(passage, vvc)
    const waveform = await waveformCreator.getPCMValues(audioSlices)
    if (!waveform.length) {
        return ''
    }
    const buffer = AudioContextFactory.getAudioContext().createBuffer(1, waveform.length, 44100)
    buffer.copyToChannel(waveform, 0)
    const wavBlob = await convertAudioBufferToWav(buffer)

    // allow garbage collection to take place for these temporary blobs that were used to create wavBlob
    vvc.viewableVideos.forEach((vv) => URL.revokeObjectURL(vv.src))

    return URL.createObjectURL(wavBlob)
}
